import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ActiveFilterDescriptor } from "@/components/data-table/utils/activeFilterDescriptors";
import { formatActiveFilterSummaryLine } from "@/components/data-table/utils/activeFilterDescriptors";
import { X } from "lucide-react";
import { memo, useCallback, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { ActiveFiltersSummaryColumnVisibilityToggle } from "./ActiveFiltersSummaryColumnVisibilityToggle";
import { activeFiltersSummaryChipRemoveButtonClassName } from "./activeFiltersSummaryChipButtonClasses";

interface ActiveFiltersSummaryFilterChipProps {
  item: ActiveFilterDescriptor;
  /**
   * `surface` — pill `Badge` chip (truncate + inline remove): used in the data table empty state
   *  where filters wrap in a wide area.
   * `popover` — compact `<li>` row (stacked label + summary, separators): used inside
   * `ActiveFiltersSummaryList` popover / sheet panels.
   */
  layout: "surface" | "popover";
  columnTitle: string;
  onRemove: () => void;
  /** When set, eye control toggles column visibility via TanStack Table (`getCanHide`). */
  visibilityToggle?: { isVisible: boolean; onToggle: () => void };
}

const ActiveFiltersSummaryFilterChipComp = ({
  item,
  layout,
  columnTitle,
  onRemove,
  visibilityToggle,
}: ActiveFiltersSummaryFilterChipProps) => {
  const { t } = useTranslation();
  const handleRemoveClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRemove();
    },
    [onRemove],
  );

  const summary = formatActiveFilterSummaryLine(
    t,
    item.value,
    item.filterValueTranslationKey,
  );
  const isSurface = layout === "surface";

  const visibilityTitle = visibilityToggle
    ? visibilityToggle.isVisible
      ? t("table.activeFilters.hideFilteredColumn")
      : t("table.activeFilters.showFilteredColumn")
    : "";

  const visibilityControl = visibilityToggle ? (
    <ActiveFiltersSummaryColumnVisibilityToggle
      isVisible={visibilityToggle.isVisible}
      title={visibilityTitle}
      onToggle={visibilityToggle.onToggle}
    />
  ) : null;

  if (isSurface) {
    return (
      <Badge
        variant="outline"
        className="inline-flex h-auto max-w-[min(100%,30rem)] flex-nowrap items-center gap-1.5 rounded-full border-input bg-background px-0 py-0.5 pr-2 text-left align-middle font-normal leading-snug shadow-sm hover:bg-accent/40 sm:max-w-[min(100%,34rem)]"
      >
        <div className="min-w-0 flex-1 py-1 pr-0.5 pl-3">
          <div className="flex min-w-0 items-baseline gap-x-1">
            <span className="truncate text-sm font-medium text-foreground">
              {columnTitle}
            </span>
            <span className="shrink-0 text-muted-foreground opacity-70">·</span>
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {summary}
            </span>
          </div>
        </div>
        {visibilityControl}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={activeFiltersSummaryChipRemoveButtonClassName}
          onClick={handleRemoveClick}
        >
          <X strokeWidth={2.25} />
        </Button>
      </Badge>
    );
  }

  return (
    <li className="flex items-center gap-2 border-b border-border/60 px-2 py-2 text-left transition-colors last:border-b-0 hover:bg-muted/40">
      <div className="min-w-0 flex-1 py-px">
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="text-xs font-medium leading-tight text-foreground">
            {columnTitle}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs leading-snug text-muted-foreground">
          {summary}
        </p>
      </div>
      {visibilityControl}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={activeFiltersSummaryChipRemoveButtonClassName}
        onClick={handleRemoveClick}
      >
        <X strokeWidth={2.25} />
      </Button>
    </li>
  );
};

export const ActiveFiltersSummaryFilterChip = memo(
  ActiveFiltersSummaryFilterChipComp,
);
